import styled, { css, DefaultTheme } from "styled-components";
import Autocomplete from "@mui/material/Autocomplete";

const getBorder = (theme: DefaultTheme, isError: boolean, edit: boolean) => {
  if (isError) {
    return `1px solid ${theme.redLight}`;
  }
  if (edit) {
    return "1px solid #096dd9";
  }
  return "1px solid #e7ebf2";
};

export const FieldContainer = styled.div<{
  edit: boolean;
  $isError: boolean;
  $cardDisplay?: boolean;
}>`
  padding: 5px 0px 5px 0px;
  width: 50%;

  input {
    border: none;
    width: 100% !important;
    border-bottom: ${({ theme, $isError, edit }) =>
      getBorder(theme, $isError, edit)};
    padding-bottom: 5px;
    color: ${({ theme }) => theme.black};
    align-self: center;
    font-size: 15px;
  }
  input:focus {
    outline: none;
  }
  input::placeholder {
    color: ${({ $isError, theme }) =>
      $isError ? theme.redLight : theme.black};
  }
  input:disabled {
    background-color: white;
  }

  input[type="date"] {
    box-sizing: border-box;
    outline: 0;
    position: relative;
    width: 100%;
    color: ${({ $isError, theme }) =>
      $isError ? theme.redLight : theme.black};
  }

  input[type="date"]::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    color: transparent;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
  }

  .MuiAutocomplete-root {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 10px 0;
  }

  ${({ $cardDisplay, theme }) =>
    $cardDisplay &&
    css`
      box-sizing: border-box;
      border-bottom: 1px solid ${theme.neutral100};
      min-height: 48px;
      padding: 4px 0 0;
      width: 100%;
    `}
`;

export const FieldInput = styled.input<{
  $cardDisplay: boolean;
  $isError: boolean;
  $edit: boolean;
}>`
  ${({ $cardDisplay, $isError, $edit, theme }) =>
    $cardDisplay &&
    css`
      && {
        border-bottom: ${$isError || $edit
          ? getBorder(theme, $isError, $edit)
          : "0"};
        color: ${$isError && $edit ? theme.redLight : theme.greyMedium};
        font-family: "bold";
        font-size: 16px;
        line-height: 24px;
        padding: 0;
        -webkit-text-fill-color: ${$isError && $edit
          ? theme.redLight
          : theme.greyMedium};
        opacity: 1;
      }
    `}
`;

const getFieldTitleColor = (
  theme: DefaultTheme,
  newRequisition?: boolean,
  isError?: boolean,
) => {
  if (isError) {
    return theme.redDark;
  }
  if (newRequisition) {
    return theme.blueDark;
  }
  if (isError) {
    return "#5d5a5a";
  }
};

export const FieldTitle = styled.div<{
  $isError: boolean;
  $cardDisplay?: boolean;
  newRequisition?: boolean;
}>`
  font-size: ${({ newRequisition }) => (newRequisition ? "12px" : "17px")};
  color: ${({ $isError, theme, newRequisition }) =>
    getFieldTitleColor(theme, newRequisition, $isError)};
  display: flex;
  align-items: center;
  grid-gap: 3px;
  font-family: ${({ newRequisition }) => newRequisition && "light"};

  svg {
    width: 16px;
    margin-top: -5px;
  }

  ${({ $cardDisplay, $isError, theme }) =>
    $cardDisplay &&
    css`
      color: ${$isError ? theme.redDark : theme.greyLight};
      font-family: "light";
      font-size: 14px;
      line-height: 20px;

      svg {
        height: 16px;
        margin-top: 0;
        flex-shrink: 0;
      }
    `}
`;

export const FieldValue = styled.div`
  font-size: 16px;
`;

export const FieldDisplayValue = styled.div`
  color: ${({ theme }) => theme.greyMedium};
  font-family: "bold";
  font-size: 16px;
  line-height: 24px;
  overflow-wrap: anywhere;
`;

export const ChoiceInputRow = styled.div`
  display: flex;
  grid-gap: 5px;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.neutral100};
  padding-bottom: 6px;

  input {
    width: auto;
  }
`;

export const RadioInputField = styled.div`
  display: flex;
  grid-gap: 5px;
`;

export const CheckboxInputField = styled.div`
  display: flex;
  grid-gap: 5px;
  input[type="checkbox"] {
    width: 16px !important;
    height: 16px !important;
  }
`;

export const AutoCompleteTextfieldWrapper = styled.div`
  && {
    .MuiAutocomplete-input {
      border-bottom: none !important;
    }

    .MuiAutocomplete-inputRoot:not(.Mui-focused):not(.Mui-disabled),
    .MuiAutocomplete-inputRoot:not(.Mui-focused):not(.Mui-disabled)
      .MuiAutocomplete-input {
      cursor: pointer;
    }
  }
`;

export const AutocompleteOptionReason = styled.span`
  color: ${({ theme }) => theme.greyDark};
  flex-shrink: 0;
  font-size: 12px;
  margin-left: auto;
  padding-left: 12px;
  white-space: nowrap;
`;

export const AUTOCOMPLETE_POPPER_Z_INDEX = 1400;

export const AUTOCOMPLETE_LISTBOX_PROPS = {
  style: { maxHeight: 200 },
} as const;

export const FormFieldAutocomplete = styled(Autocomplete)`
  display: inline-block;

  & input {
    width: 200px;
    background-color: ${({ theme }) => theme.white};
    color: ${({ theme }) => theme.black};
  }
` as typeof Autocomplete;
